import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useMemo } from 'react'

function useOfferPageBedbankRates(effectiveRates: Array<App.BedbankRate>, checkInDate?: string, checkOutDate?: string) {
  const nonRefundableRates = effectiveRates.filter(rate => !rate.partiallyRefundable && !rate.refundable)
  const nonRefundableRatesBoardCodes = useMemo(() => new Set(nonRefundableRates.map(rate => rate.boardCode)), [nonRefundableRates])

  const canTriggerFlexCfmrBedbankPackagesExperiment = !!nonRefundableRates.length && !!checkInDate && !!checkOutDate
  const isFlexCfmrBedbankPackagesEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croFlexCfmrBedbankPackages, canTriggerFlexCfmrBedbankPackagesExperiment)

  /**
   * Rate filtering logic for CFMR flex packages:
   *
   * When non-refundable rates exist, we offer CFMR inclusion on these rates.
   * To prevent duplicate options, we filter out:
   * - Refundable rates that have the same board code as non-refundable rates
   * This ensures customers see unique rate options without redundant board types
   *
   * When only refundable rates are available, we show all rates since CFMR flex
   * packages aren't applicable (no non-refundable base rates to upgrade from).
   */
  const rates = useMemo(() => {
    const hasNonRefundableRates = nonRefundableRates.length > 0

    // Show all rates when no non-refundable rates exist (no CFMR flex scenario) or the AB test is off
    if (!hasNonRefundableRates || !isFlexCfmrBedbankPackagesEnabled) {
      return effectiveRates
    }

    // Filter out refundable rates that duplicate non-refundable board codes
    return effectiveRates.filter(rate => {
      const isRefundableRate = rate.partiallyRefundable || rate.refundable
      const hasMatchingNonRefundableBoardCode = nonRefundableRatesBoardCodes.has(rate.boardCode)

      if (isRefundableRate) {
        return !hasMatchingNonRefundableBoardCode
      }

      return true
    })
  }, [effectiveRates, nonRefundableRates.length, nonRefundableRatesBoardCodes, isFlexCfmrBedbankPackagesEnabled])

  return {
    rates,
    isFlexCfmrBedbankPackagesEnabled,
  }
}

export default useOfferPageBedbankRates
